// ordered list of cc columns
import { LSCField } from '../../../models/LSCFields';
import { ColDef, GetRowIdParams, GridOptions, ITextFilterParams, RowClassParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { CCLocation } from '../../../models/locations-models/cc-location';
import { getFullNameForFieldID } from '../location-utils';
import { AgBasicHeaderComponent } from '../../global/gx-ag-grid/ag-basic-header/ag-basic-header.component';
import { StoreCodeCellComponent } from './cells/store-code-cell/store-code-cell.component';
import { AgTextFloatingFilterComponent } from '../../global/gx-ag-grid/ag-text-floating-filter/ag-text-floating-filter.component';
import { LinkCellComponent } from '../location-list/cells/link-cell/link-cell.component';
import { CcRowMenuCellComponent } from './cells/cc-row-menu-cell/cc-row-menu-cell.component';
import { DEFAULT_GRID_COLUMN_DEFS, DEFAULT_GRID_OPTIONS } from '../constants';
import { FormatDistancePipe } from '../../../pipes/format-distance.pipe';
import { CcRowMenuHeaderComponent } from './headers/cc-row-menu-header/cc-row-menu-header.component';

const formatDistance = new FormatDistancePipe();

export enum CC_SPECIFIC_FIELD {
  DISTANCE_FROM_FOCUSED_LOCATION = 'distanceFromFocusedLocationInMeters',
  CC_ROW_MENU = 'ccRowMenu'
}

export const CC_DEFAULT_COLUMN_DEFS: ColDef<CCLocation> = {
  ...DEFAULT_GRID_COLUMN_DEFS,
  resizable: true,
  autoHeight: true,
  wrapText: true,
};

export const CC_GRID_OPTIONS: GridOptions = {
  ...DEFAULT_GRID_OPTIONS,
  suppressCellFocus: true,
  suppressDragLeaveHidesColumns: true,
  suppressRowHoverHighlight: true, // handled by local effect
  getRowId: (params: GetRowIdParams<CCLocation>) => String(params?.data?.placeId),
  rowSelection: {
    mode: 'multiRow',
    // selects rows that pass active filters and are on the current page
    selectAll: 'currentPage'
  },
  getRowClass: (params: RowClassParams<CCLocation>) =>
      params.data?.isMarkedAsDuplicate ? 'duplicateRow' : '',
  selectionColumnDef: {
    sortable: false,
    minWidth: 66,
    maxWidth: 66,
    pinned: 'left',
    lockPinned: true,
    // vertical alignment same as any other cell
    cellStyle: { paddingTop: '15px', paddingBottom: '15px' },
    headerClass: 'selectHeader',
  },
};

/**
 * Ordered CC Column Definitions
 */
export const CC_COLUMN_DEFS: ColDef<CCLocation>[] = [
  {
    colId: LSCField.storeCode,
    field: LSCField.storeCode,
    headerName: getFullNameForFieldID(LSCField.storeCode),
    headerComponent: AgBasicHeaderComponent,
    cellRenderer: StoreCodeCellComponent,
    width: 190,
    cellStyle: { paddingTop: '10px', paddingBottom: '10px', lineHeight: '28px' },
  },
  {
    colId: LSCField.businessName,
    field: LSCField.businessName,
    headerName: getFullNameForFieldID(LSCField.businessName),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellDataType: 'text',
    width: 250,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: LSCField.addressLine1,
    field: LSCField.addressLine1,
    headerName: getFullNameForFieldID(LSCField.addressLine1),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellDataType: 'text',
    width: 210,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: LSCField.addressLine2,
    field: LSCField.addressLine2,
    headerName: getFullNameForFieldID(LSCField.addressLine2),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellDataType: 'text',
    width: 210,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: LSCField.city,
    field: LSCField.city,
    headerName: getFullNameForFieldID(LSCField.city),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellDataType: 'text',
    width: 170,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: CC_SPECIFIC_FIELD.DISTANCE_FROM_FOCUSED_LOCATION,
    field: CC_SPECIFIC_FIELD.DISTANCE_FROM_FOCUSED_LOCATION,
    headerName: 'Distance from focused location',
    headerComponent: AgBasicHeaderComponent,
    valueFormatter: (params: ValueFormatterParams<CCLocation>) =>
        formatDistance.transform(params?.value),
    cellDataType: 'text',
    width: 250,
  },
  {
    colId: LSCField.website,
    field: LSCField.website,
    headerName: getFullNameForFieldID(LSCField.website),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellRenderer: LinkCellComponent,
    cellDataType: 'text',
    width: 170,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: LSCField.primaryCategory,
    field: LSCField.primaryCategory,
    headerName: getFullNameForFieldID(LSCField.primaryCategory),
    headerComponent: AgBasicHeaderComponent,
    headerComponentParams: {
      enableMenu: true,
    },
    cellDataType: 'text',
    width: 248,
    filter: true,
    filterParams: {
      buttons: ['reset'],
      trimInput: true,
    } as ITextFilterParams,
    floatingFilter: true,
    floatingFilterComponent: AgTextFloatingFilterComponent,
  },
  {
    colId: CC_SPECIFIC_FIELD.CC_ROW_MENU,
    headerName: '',
    headerComponent: CcRowMenuHeaderComponent,
    valueGetter: (params: ValueGetterParams<CCLocation>) => params,
    cellRenderer: CcRowMenuCellComponent,
    pinned: 'right',
    sortable: false,
    lockPinned: true,
    minWidth: 244,
    maxWidth: 244,
    cellStyle: {
      paddingTop: '3px',
      paddingBottom: '3px',
      paddingLeft: '5px',
      paddingRight: '5px',
    },
    resizable: false
  }
]